import { useEffect, useRef, useState } from 'react'
import dayjs from 'dayjs'
import toast from 'react-hot-toast'
import type { JSONContent } from '@tiptap/react'
import { Save, CheckCircle2 } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import RichTextEditor, { EMPTY_DOC } from './RichTextEditor'
import NotesHistoryList from './NotesHistoryList'
import { isTiptapDocEmpty } from '@/lib/tiptapContent'
import {
  useGetProjectNotesQuery,
  useSaveProjectNoteMutation,
} from '@/redux/service/project'

interface ProjectNotesDrawerProps {
  projectId: number | null
  projectName: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

const formatTimestamp = (iso: string) => {
  const d = dayjs(iso)
  return d.isValid() ? d.format('MMM D, YYYY [at] h:mm a') : ''
}

const ProjectNotesDrawer: React.FC<ProjectNotesDrawerProps> = ({
  projectId,
  projectName,
  open,
  onOpenChange,
}) => {
  const { data, isLoading } = useGetProjectNotesQuery(projectId ?? -1, {
    skip: !open || projectId == null,
    refetchOnMountOrArgChange: true,
  })
  const [saveNote, { isLoading: isSaving }] = useSaveProjectNoteMutation()

  const current = data?.data?.current ?? null
  const history = data?.data?.history ?? []

  const [draft, setDraft] = useState<JSONContent>(EMPTY_DOC)
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)
  // ponytail: ref (not state) so it doesn't retrigger the sync effect below
  const dirtySinceSyncRef = useRef(false)
  const ignoreNextUpdateRef = useRef(false)
  const lastSavedContentRef = useRef<JSONContent>(EMPTY_DOC)

  useEffect(() => {
    if (dirtySinceSyncRef.current) return

    const latest = (current?.content as JSONContent) ?? EMPTY_DOC

    ignoreNextUpdateRef.current = true
    setDraft(latest)
    lastSavedContentRef.current = latest
    dirtySinceSyncRef.current = false
  }, [current])
  useEffect(() => {
    if (!open) {
      setDraft(EMPTY_DOC)
      lastSavedContentRef.current = EMPTY_DOC
      dirtySinceSyncRef.current = false
    }
  }, [open])
  const isDirty =
    JSON.stringify(draft) !== JSON.stringify(lastSavedContentRef.current)
  const isEmpty = isTiptapDocEmpty(draft)

  const requestOpenChange = (next: boolean) => {
    if (!next && isDirty) {
      setShowDiscardConfirm(true)
      return
    }
    onOpenChange(next)
  }

  const handleSave = async () => {
    if (projectId == null) return
    if (isEmpty) {
      toast.error('Note cannot be empty')
      return
    }
    try {
      const res = await saveNote({
        project_id: projectId,
        content: draft,
      }).unwrap()

      lastSavedContentRef.current = draft
      dirtySinceSyncRef.current = false

      toast.success(res.data.unchanged ? 'No changes to save' : 'Note saved')
    } catch (err) {
      const message =
        (err as { data?: { message?: string } })?.data?.message ??
        'Failed to save note'
      toast.error(message)
    }
  }

  return (
    <>
      <Sheet open={open} onOpenChange={requestOpenChange}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>
              Notes{projectName ? ` — ${projectName}` : ''}
            </SheetTitle>
          </SheetHeader>

          <div className='flex flex-1 min-h-0 flex-col gap-3'>
            <RichTextEditor
              content={draft}
              editable
              onChange={next => {
                if (ignoreNextUpdateRef.current) {
                  ignoreNextUpdateRef.current = false
                  return
                }

                dirtySinceSyncRef.current = true
                setDraft(next)
              }}
            />

            <div className='flex items-center justify-between gap-3'>
              {current ? (
                <span className='flex items-center gap-1.5 text-xs text-muted-foreground'>
                  <CheckCircle2 className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
                  Last saved by{' '}
                  <span className='font-medium text-foreground'>
                    {current.created_by_name}
                  </span>{' '}
                  on {formatTimestamp(current.created_at)}
                </span>
              ) : (
                <span className='text-xs text-muted-foreground'>
                  No notes yet for this project.
                </span>
              )}
              <Button
                type='button'
                size='sm'
                onClick={handleSave}
                disabled={isSaving || isEmpty || !isDirty}
                className='h-8 shrink-0 text-xs'
              >
                <Save className='mr-1.5 h-3.5 w-3.5' />
                {isSaving ? 'Saving…' : 'Save Note'}
              </Button>
            </div>

            <div className='min-h-0 flex-1 overflow-y-auto border-t border-border pt-3'>
              <p className='mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                Notes History
              </p>
              {isLoading ? (
                <p className='py-6 text-center text-xs text-muted-foreground'>
                  Loading…
                </p>
              ) : (
                <NotesHistoryList history={history} />
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={showDiscardConfirm} onOpenChange={setShowDiscardConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Discard unsaved changes?</DialogTitle>
            <DialogDescription>
              You have unsaved changes to this note. Closing now will discard
              them.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className='gap-2'>
            <DialogClose asChild>
              <Button variant='outline'>Keep editing</Button>
            </DialogClose>
            <Button
              variant='destructive'
              onClick={() => {
                dirtySinceSyncRef.current = false
                setShowDiscardConfirm(false)
                onOpenChange(false)
              }}
            >
              Discard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default ProjectNotesDrawer
